import { Directive, output } from '@angular/core';

/** How far a finger has to travel sideways, and how little up or down, before it counts as a swipe. */
const MIN_HORIZONTAL_DISTANCE = 80;
const MAX_VERTICAL_DISTANCE = 60;

/**
 * A horizontal swipe with a finger or pen: `swipedRight` (the finger moves to the right, "back") and
 * `swipedLeft` ("next"). A mouse never swipes.
 *
 * The host gets `touch-action: pan-y`: the browser keeps vertical scrolling, and leaves a sideways gesture to
 * the page. Without it a real phone takes the gesture over and cancels the pointer before it ends.
 * A gesture that starts in a field or in something that scrolls sideways itself is left alone.
 */
@Directive({
  selector: '[appSwipe]',
  standalone: true,
  host: {
    style: 'touch-action: pan-y',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointerup)': 'onPointerUp($event)',
    '(pointercancel)': 'start = null',
  },
})
export class SwipeDirective {
  readonly swipedLeft = output<void>();
  readonly swipedRight = output<void>();

  protected start: { x: number; y: number } | null = null;

  protected onPointerDown(event: PointerEvent): void {
    const ignored = event.pointerType === 'mouse' || startsInOwnGesture(event.target);
    this.start = ignored ? null : { x: event.clientX, y: event.clientY };
  }

  protected onPointerUp(event: PointerEvent): void {
    const start = this.start;
    this.start = null;

    if (!start || Math.abs(event.clientY - start.y) > MAX_VERTICAL_DISTANCE) {
      return;
    }

    const horizontalDistance = event.clientX - start.x;

    if (horizontalDistance > MIN_HORIZONTAL_DISTANCE) {
      this.swipedRight.emit();
    } else if (horizontalDistance < -MIN_HORIZONTAL_DISTANCE) {
      this.swipedLeft.emit();
    }
  }
}

/** Fields select text and sideways tracks scroll: a swipe there belongs to them, not to the page. */
function startsInOwnGesture(target: EventTarget | null): boolean {
  let element = target instanceof Element ? target : null;

  while (element) {
    if (element.matches('input, textarea, select, [contenteditable]')) {
      return true;
    }

    if (element.scrollWidth > element.clientWidth && ['auto', 'scroll'].includes(getComputedStyle(element).overflowX)) {
      return true;
    }

    element = element.parentElement;
  }

  return false;
}
